import type { BeckyFunctionLibrary, BeckyRequestPayload, BeckyModel } from '@wix/thunderbolt-becky-root'
import type { ModuleFetcher, Logger, ProcessOoiCssFunctionLib } from '@wix/thunderbolt-becky-types'
import { fetchModules } from './moduleResolverFactory'
import { decodeModulesToLoad, processOoiCss, ooiStyleParamsUtil } from '@wix/thunderbolt-commons'

const resolveWidgetsToLoad = (ooiVersions: BeckyRequestPayload['ooiVersions'], widgetIdsOnPage: Array<string> = []) => {
	const allWidgetsOnSite = decodeModulesToLoad(ooiVersions || '')
	return allWidgetsOnSite.filter((w) => widgetIdsOnPage.find((widgetId) => widgetId === w.id))
}

const staticCssProcessorMemoizer = (getBuildTimeStaticCss: (cssConfig: object) => { css: string; hash?: string }) => {
	const staticCssHashes: { [hash: string]: boolean } = {}
	return (cssConfig: object) => {
		const { css, hash } = getBuildTimeStaticCss(cssConfig)
		if (!hash) {
			// backward-compatibility: cssConfig might not include the new build-time hashing feature (in tpa-webpack-plugin)
			return css
		}
		if (staticCssHashes[hash]) {
			// since staticCss already exists, we don't to return it
			return ''
		}
		staticCssHashes[hash] = true
		return css
	}
}

function processOoiCssFactory({
	runAndReport,
	widgetsWithAppSettings,
	isResponsive,
	anywhereThemeOverride,
	shouldRemoveStaticCss,
}: {
	runAndReport: BeckyFunctionLibrary['runAndReport']
	widgetsWithAppSettings: Record<string, any> | undefined
	isResponsive: boolean
	anywhereThemeOverride?: string
	shouldRemoveStaticCss: boolean
}): ProcessOoiCssFunctionLib['processOoiCss'] {
	// can't use typescript import due to jest transfer exploding when looking into tpa-style-webpack-plugin (and es6 module)
	const { getBuildTimeStaticCss } = require('@wix/tpa-style-webpack-plugin/standalone')
	const staticCssProcessor = staticCssProcessorMemoizer(getBuildTimeStaticCss)
	return (
		widgetId: string,
		styleData: any,
		{ prefixSelector, isRTL, isMobile, usesCssPerBreakpoint, dimensions, cssConfig },
		referenceId: string | undefined,
		tpaData: any
	): string => {
		let { style: styleParams } = styleData

		if (ooiStyleParamsUtil.shouldOverrideStyleParams(widgetId)) {
			const appSettings = widgetsWithAppSettings?.[`${widgetId}-${referenceId}`]
			styleParams = ooiStyleParamsUtil.overrideStyleParams({
				styleParams,
				widgetId,
				appSettings,
				tpaData,
				isResponsive,
				anywhereThemeOverride,
			})
			styleData.style = styleParams
		}

		if (!cssConfig) {
			return ''
		}

		return processOoiCss({
			widgetId,
			styleData,
			prefixSelector,
			cssConfig,
			isRTL,
			isMobile,
			dimensions,
			usesCssPerBreakpoint,
			getStaticCss: staticCssProcessor,
			shouldRemoveStaticCss,
			runAndReport,
			tpaData,
		})
	}
}

export const processOoiCssImpl = async ({
	funcLib: { runAndReport },
	beckyModel,
}: {
	payload: Pick<BeckyRequestPayload, 'ooiVersions' | 'cssVarsOnlyWidgets'>
	funcLib: BeckyFunctionLibrary
	beckyModel: BeckyModel
}) => {
	return processOoiCssFactory({
		runAndReport,
		widgetsWithAppSettings: beckyModel.ooiWidgetsAppSettings,
		isResponsive: beckyModel.isResponsive,
		anywhereThemeOverride: beckyModel.anywhereThemeOverride,
		shouldRemoveStaticCss: !!beckyModel.experiments['specs.thunderbolt.ooiCssAsLinkTag'],
	})
}

export const reportCssVarsMigrationStatus = (
	widgetsCssConfigs: BeckyModel['ooiCssConfigStore'],
	cssVarsOnlyWidgets: Array<string>,
	logger: Logger
) => {
	Object.entries(widgetsCssConfigs).forEach(([widgetId, cssConfig]) => {
		if (!cssConfig.css?.trim()) {
			logger.info(`processOoiCssFactory - cssVars=true`, { widgetId })
			if (!cssVarsOnlyWidgets.includes(widgetId)) {
				logger.info(`processOoiCssFactory - cssVars=true & cssPerBreakpoint=false`, { widgetId })
			}
		} else {
			if (cssVarsOnlyWidgets.includes(widgetId)) {
				logger.info(`processOoiCssFactory - BAD - cssVars=false & cssPerBreakpoint=true`, {
					widgetId,
					cssConfigCssLength: cssConfig.css.length,
				})
			} else {
				logger.info(`processOoiCssFactory - cssVars=false & cssPerBreakpoint=false`, {
					widgetId,
					cssConfigCssLength: cssConfig.css.length,
				})
			}
		}
	})
}

export const loadCssConfigs = async (
	ooiVersions: string,
	widgetsIds: Array<string>,
	moduleFetcher: ModuleFetcher,
	runAsyncAndReport: any,
	logger: Logger
) => {
	const cssConfigModulesToLoad = resolveWidgetsToLoad(ooiVersions, widgetsIds)
	const loadedModules = await runAsyncAndReport(
		() =>
			fetchModules(
				cssConfigModulesToLoad,
				{
					runAsyncAndReport,
					moduleFetcher,
				},
				logger
			),
		`tb-css-fetch-cssConfig-modules-${Object.keys(cssConfigModulesToLoad).join(',')}`
	)

	return Object.keys(loadedModules).reduce((acc, widgetId) => {
		const module = loadedModules[widgetId]
		if (module?.cssConfig) {
			acc[widgetId] = module.cssConfig
		}
		return acc
	}, {} as BeckyModel['ooiCssConfigStore'])
}
